import { Component } from '@theme/component';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A swatch library card. Reveals a fabric-details panel when the card is
 * clicked anywhere outside its controls, or the "Fabric details" control is
 * activated, and hides it again on toggle, close, or Escape. On desktop the panel slides up over the swatch board; on mobile it
 * becomes a full-screen modal (big swatch image, info below), so opening also
 * locks body scroll, moves focus into the panel, and promotes it to a dialog.
 *
 * State lives in the `open` attribute on the host element; CSS keys the panel
 * animation and the toggle arrow off `:host([open])` / `.swatch-card[open]`.
 *
 * @typedef {Object} Refs
 * @property {HTMLButtonElement} [toggle] - The "Fabric details" disclosure button
 * @property {HTMLElement} [panel] - The sliding details panel
 * @property {HTMLButtonElement} [closeButton] - The panel's close button
 *
 * @extends {Component<Refs>}
 */
class SwatchCard extends Component {
  /** Whether this card is the one currently holding the body scroll lock. */
  #scrollLocked = false;

  /** Where the pointer went down, so a carousel swipe isn't read as a tap. */
  #pointerOrigin = /** @type {{ x: number, y: number } | null} */ (null);

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#handleKeydown);
    this.addEventListener('pointerdown', this.#handlePointerDown);
    this.addEventListener('click', this.#suppressLabelActivation);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#handleKeydown);
    this.removeEventListener('pointerdown', this.#handlePointerDown);
    this.removeEventListener('click', this.#suppressLabelActivation);
    this.#unlockScroll();
  }

  /**
   * Open the details panel from a click anywhere on the card. Clicks on the
   * card's own controls (details toggle, add-to-cart) and inside the open
   * panel are left alone, as are swipes on the mobile carousel.
   *
   * @param {PointerEvent} event
   */
  expand(event) {
    if (this.hasAttribute('open')) return;

    // `event.target` is proxied to the element carrying the `on:click`, so the
    // real clicked node has to come from the composed path.
    const target = event.composedPath?.()[0];

    if (!(target instanceof Element)) return;
    if (target.closest('a, button, input, select, textarea, .swatch-card__panel')) return;
    if (this.#isDrag(event)) return;
    if (!window.getSelection()?.isCollapsed) return;

    this.open();
  }

  /** Toggle the fabric-details panel open or closed. */
  toggle() {
    if (this.hasAttribute('open')) {
      this.close();
    } else {
      this.open();
    }
  }

  /** Reveal the fabric-details panel. */
  open() {
    this.setAttribute('open', '');
    this.refs.toggle?.setAttribute('aria-expanded', 'true');

    if (isMobileBreakpoint()) {
      this.refs.panel?.setAttribute('role', 'dialog');
      this.refs.panel?.setAttribute('aria-modal', 'true');
      this.#lockScroll();
      this.refs.closeButton?.focus();
    }
  }

  /** Hide the panel and return focus to the toggle. */
  close() {
    if (!this.hasAttribute('open')) return;
    this.removeAttribute('open');
    this.refs.toggle?.setAttribute('aria-expanded', 'false');
    this.refs.panel?.setAttribute('role', 'group');
    this.refs.panel?.removeAttribute('aria-modal');
    this.#unlockScroll();
    this.refs.toggle?.focus();
  }

  #lockScroll() {
    if (this.#scrollLocked) return;
    this.#scrollLocked = true;
    document.body.style.setProperty('overflow', 'hidden');
  }

  #unlockScroll() {
    if (!this.#scrollLocked) return;
    this.#scrollLocked = false;
    document.body.style.removeProperty('overflow');
  }

  /**
   * In the PDP variant picker the card lives inside the swatch's `<label>`, so
   * a click on the board would otherwise activate the radio and swap the
   * selected variant out from under the popup. Clicks on the card belong to the
   * card; the swatch itself stays the way to choose a variant.
   *
   * @param {MouseEvent} event
   */
  #suppressLabelActivation = (event) => {
    if (this.closest('label')) event.preventDefault();
  };

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    this.#pointerOrigin = { x: event.clientX, y: event.clientY };
  };

  /**
   * Whether the pointer travelled far enough between press and release to be a
   * scroll/swipe on the mobile carousel rather than a tap on the card.
   *
   * @param {PointerEvent} event
   */
  #isDrag(event) {
    const origin = this.#pointerOrigin;
    this.#pointerOrigin = null;

    if (!origin || typeof event.clientX !== 'number') return false;

    return Math.hypot(event.clientX - origin.x, event.clientY - origin.y) > 10;
  }

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key === 'Escape' && this.hasAttribute('open')) {
      event.stopPropagation();
      this.close();
    }
  };
}

if (!customElements.get('swatch-card')) {
  customElements.define('swatch-card', SwatchCard);
}
